import { useState, useEffect } from 'react';
import { FormItem, FormLabel, FormDescription } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Smartphone } from "lucide-react";
import { pushNotificationService } from '@/services/pushNotificationService';
import { useBuyer } from '@/contexts/BuyerContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface PushNotificationsFieldProps {
  isEditing: boolean;
}

const PushNotificationsField = ({ isEditing }: PushNotificationsFieldProps) => {
  const { buyerProfile, updateBuyerProfile } = useBuyer();
  // Type assertion for new column not yet in generated types
  const profile = buyerProfile as any;
  const [isEnabled, setIsEnabled] = useState(profile?.push_notifications_enabled ?? true);
  const [isLoading, setIsLoading] = useState(false);
  const isPushAvailable = pushNotificationService.isAvailable();

  useEffect(() => {
    if (profile) {
      setIsEnabled(profile.push_notifications_enabled ?? true);
    }
  }, [buyerProfile]);

  const handleToggle = async (checked: boolean) => {
    if (!buyerProfile?.user_id) return;
    
    setIsLoading(true);
    try {
      // If enabling, also request permission and register token
      if (checked && isPushAvailable) {
        const initialized = await pushNotificationService.initialize();
        if (initialized) {
          await pushNotificationService.registerToken(buyerProfile.user_id, 'buyer');
        }
      }

      // Update the database
      const success = await pushNotificationService.updatePushEnabled(
        buyerProfile.user_id,
        'buyer',
        checked
      );

      if (success) {
        setIsEnabled(checked);
        toast.success(checked ? 'Push notifications enabled' : 'Push notifications disabled');
      } else {
        toast.error('Failed to update push notification settings');
      }
    } catch (error) {
      logger.error('[PushNotificationsField] Error toggling push:', error);
      toast.error('Failed to update push notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if not on a native platform
  if (!isPushAvailable) {
    return null;
  }

  return (
    <FormItem className="flex items-center justify-between rounded-lg border p-4">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-muted-foreground" />
          <FormLabel className="text-base">Push Notifications</FormLabel>
        </div>
        <FormDescription>
          Receive instant alerts on your device for new properties and offer updates
        </FormDescription>
      </div>
      <div className="flex items-center gap-2">
        {isEnabled ? (
          <Bell className="h-4 w-4 text-godyspo-gold" />
        ) : (
          <BellOff className="h-4 w-4 text-muted-foreground" />
        )}
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={!isEditing || isLoading}
        />
      </div>
    </FormItem>
  );
};

export default PushNotificationsField;
